import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import {
  Play,
  Clock,
  Loader2,
  ChevronRight,
  History,
  CheckCircle2,
  XCircle,
  CircleDashed,
  PlayCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { gql, useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { fileUrl } from '../lib/utils';
import { CONTINUE_WATCHING } from '@/lib/graphql/queries/course';

const LESSONS_QUERY = gql`
  query Lessons($limit: Int!, $page: Int) {
    courses(kind: "lesson", limit: $limit, page: $page, status: "active") {
      nodes {
        id
        title
        description
        thumbnail
        duration
        videoUrl
        category
        myStatus {
          status
          percent
          lastAttemptScorePercent
          lastAttemptPassed
          attemptsCount
        }
      }
      pageInfo {
        hasNextPage
        currentPage
        totalPages
      }
      totalCount
    }
  }
`;

const LIMIT = 12;

const formatPercent = (p: number) => Math.max(0, Math.min(100, p || 0));

type LessonStatus = 'NOT_WATCHED' | 'IN_PROGRESS' | 'FAILED' | 'PASSED';

interface MyStatus {
  status: LessonStatus;
  percent: number;
  lastAttemptScorePercent: number | null;
  lastAttemptPassed: boolean | null;
  attemptsCount: number;
}

const StatusBadge = ({ status, score }: { status: LessonStatus; score?: number | null }) => {
  switch (status) {
    case 'PASSED':
      return (
        <Badge className='bg-green-600 hover:bg-green-600 text-white border-0'>
          <CheckCircle2 className='h-3 w-3 mr-1' />
          Тэнцсэн{typeof score === 'number' ? ` · ${score}%` : ''}
        </Badge>
      );
    case 'FAILED':
      return (
        <Badge variant='destructive'>
          <XCircle className='h-3 w-3 mr-1' />
          {typeof score === 'number' ? `Тэнцээгүй · ${score}%` : 'Тэнцээгүй'}
        </Badge>
      );
    case 'IN_PROGRESS':
      return (
        <Badge variant='secondary' className='bg-blue-100 text-blue-800 hover:bg-blue-100'>
          <PlayCircle className='h-3 w-3 mr-1' />
          Үзэж байна
        </Badge>
      );
    case 'NOT_WATCHED':
    default:
      return (
        <Badge variant='outline' className='text-muted-foreground'>
          <CircleDashed className='h-3 w-3 mr-1' />
          Эхлээгүй
        </Badge>
      );
  }
};

const Academy = () => {
  const observerRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, fetchMore } = useQuery(LESSONS_QUERY, {
    variables: {
      limit: LIMIT,
      page: 1,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: continueData, loading: continueLoading } = useQuery(
    CONTINUE_WATCHING,
    {
      variables: { limit: 6 },
      fetchPolicy: 'cache-and-network',
    }
  );

  const lessons = data?.courses?.nodes || [];
  const hasNextPage = data?.courses?.pageInfo?.hasNextPage;
  const currentPage = data?.courses?.pageInfo?.currentPage || 1;
  const continueItems = continueData?.continueWatching || [];

  useEffect(() => {
    if (!hasNextPage || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, loading, currentPage]);

  const loadMore = () => {
    if (!hasNextPage || loading) return;

    fetchMore({
      variables: {
        page: currentPage + 1,
        limit: LIMIT,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          courses: {
            ...fetchMoreResult.courses,
            nodes: [...prev.courses.nodes, ...fetchMoreResult.courses.nodes],
          },
        };
      },
    });
  };

  const formatDuration = (duration?: number | string | null) => {
    if (!duration) return '00:00';

    if (typeof duration === 'string') {
      return duration;
    }

    if (typeof duration === 'number') {
      const mins = Math.floor(duration / 60);
      const secs = Math.floor(duration % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    return '00:00';
  };

  if (loading && lessons.length === 0) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <Loader2 className='h-12 w-12 animate-spin text-primary' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center text-center'>
        <p className='text-red-500 text-lg'>
          Хичээлүүдийг ачаалахад алдаа гарлаа
        </p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background py-12'>
      <div className='container mx-auto px-4'>
        <div className='max-w-7xl mx-auto'>
          <div className='mb-12 text-center'>
            <h1 className='text-4xl font-bold mb-4'>Хичээлүүд</h1>
          </div>

          {/* Continue Watching */}
          {continueItems.length > 0 && (
            <section className='mb-12'>
              <div className='flex items-center gap-2 mb-4'>
                <History className='h-5 w-5 text-primary' />
                <h2 className='text-2xl font-semibold'>Үргэлжлүүлэх</h2>
              </div>
              {continueLoading && continueItems.length === 0 ? (
                <div className='flex justify-center py-6'>
                  <Loader2 className='h-6 w-6 animate-spin text-primary' />
                </div>
              ) : (
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                  {continueItems.map((p: any) => {
                    const c = p.course;
                    if (!c) return null;
                    return (
                      <Link
                        key={p.id}
                        to={`/academy/lesson/${c.id}`}
                        className='group block'
                      >
                        <Card className='overflow-hidden hover:shadow-xl transition-all duration-300 h-full bg-card border-0 shadow-md'>
                          <div className='relative aspect-video overflow-hidden bg-black'>
                            <img
                              src={fileUrl(c.thumbnail) || '/placeholder.jpg'}
                              alt={c.title}
                              className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'
                            />
                            <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center'>
                              <div className='bg-primary rounded-full p-3 shadow-xl'>
                                <Play className='h-7 w-7 text-white fill-white' />
                              </div>
                            </div>
                            {c.myStatus && (
                              <div className='absolute top-3 left-3'>
                                <StatusBadge
                                  status={c.myStatus.status}
                                  score={c.myStatus.lastAttemptScorePercent}
                                />
                              </div>
                            )}
                            <div className='absolute bottom-3 right-3 bg-black/80 text-white px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5 backdrop-blur'>
                              <Clock className='h-3.5 w-3.5' />
                              {formatDuration(c.duration)}мин
                            </div>
                          </div>
                          <CardContent className='p-4 space-y-2'>
                            <div className='flex items-center justify-between gap-2'>
                              <h3 className='font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors flex-1'>
                                {c.title}
                              </h3>
                            </div>
                            <div className='space-y-1'>
                              <div className='flex items-center justify-between text-xs text-muted-foreground'>
                                <span>{Math.round(formatPercent(p.percent))}% үзсэн</span>
                                <span>
                                  {formatDuration(p.position)} / {formatDuration(c.duration)}
                                </span>
                              </div>
                              <Progress value={formatPercent(p.percent)} />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* All Lessons */}
          <section>
            <h2 className='text-2xl font-semibold mb-6'>Бүх хичээл</h2>

            {lessons.length === 0 ? (
              <div className='text-center py-20'>
                <p className='text-muted-foreground text-xl'>
                  Одоогоор хичээл байхгүй байна.
                </p>
              </div>
            ) : (
              <>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8'>
                  {lessons.map((lesson: any) => {
                    const my: MyStatus | undefined = lesson.myStatus;
                    return (
                    <Link
                      key={lesson.id}
                      to={`/academy/lesson/${lesson.id}`}
                      className='group block'
                    >
                      <Card className='overflow-hidden hover:shadow-2xl transition-all duration-300 h-full bg-card border-0 shadow-lg'>
                        <div className='relative aspect-video overflow-hidden bg-black'>
                          <img
                            src={fileUrl(lesson.thumbnail) || '/placeholder.jpg'}
                            alt={lesson.title}
                            className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'
                          />
                          <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center'>
                            <div className='bg-primary rounded-full p-5 shadow-2xl animate-pulse'>
                              <Play className='h-10 w-10 text-white fill-white' />
                            </div>
                          </div>
                          {my && (
                            <div className='absolute top-3 left-3'>
                              <StatusBadge
                                status={my.status}
                                score={my.lastAttemptScorePercent}
                              />
                            </div>
                          )}
                          <div className='absolute bottom-3 right-3 bg-black/80 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 backdrop-blur'>
                            <Clock className='h-3.5 w-3.5' />
                            {lesson.duration}мин
                          </div>
                        </div>

                        <CardContent className='p-6 space-y-2'>
                          <h3 className='font-bold text-lg mb-3 line-clamp-2 group-hover:text-primary transition-colors'>
                            {lesson.title}
                          </h3>
                          {my && my.status === 'IN_PROGRESS' && (
                            <div className='space-y-1'>
                              <div className='flex items-center justify-between text-xs text-muted-foreground'>
                                <span>{Math.round(formatPercent(my.percent))}% үзсэн</span>
                              </div>
                              <Progress value={formatPercent(my.percent)} />
                            </div>
                          )}
                          {my && my.attemptsCount > 0 && (
                            <p className='text-xs text-muted-foreground'>
                              Оролдлого: {my.attemptsCount}
                            </p>
                          )}
                          <p
                            className='text-sm text-muted-foreground line-clamp-3'
                            dangerouslySetInnerHTML={{
                              __html:
                                lesson.description || 'Тодорхойлолт байхгүй',
                            }}
                          ></p>
                        </CardContent>
                      </Card>
                    </Link>
                    );
                  })}
                </div>

                <div ref={observerRef} className='py-12'>
                  {hasNextPage && (
                    <div className='flex flex-col items-center gap-6'>
                      {loading && (
                        <Loader2 className='h-10 w-10 animate-spin text-primary' />
                      )}
                      <Button
                        onClick={loadMore}
                        disabled={loading}
                        size='lg'
                        variant='outline'
                        className='min-w-[200px]'
                      >
                        {loading ? (
                          <>
                            <Loader2 className='mr-2 h-5 w-5 animate-spin' />
                            Ачаалж байна...
                          </>
                        ) : (
                          <>
                            Илүү их хичээл үзэх
                            <ChevronRight className='ml-2 h-5 w-5' />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Academy;
